import "ts-error-as-value/lib/globals";
import { Result, ok, err } from "ts-error-as-value";
import { importJWK, JWTPayload, jwtVerify, SignJWT, decodeJwt as decodeJwtJose } from "jose";


export const decodeJwt = (
  token: string
): JWTPayload => decodeJwtJose(token);

/**
 * Checks if a JWT token is expired
 *
 * @param tokenOrPayload - The JWT token or payload to check
 * @returns true if the token is expired, false otherwise
 */
export const isJwtExpired = (
  tokenOrPayload?: string | { exp?: number }
): boolean => {
  if (!tokenOrPayload) {
    return true; // Treat empty or undefined tokens as expired
  }
  try {
    // Decode the JWT to get the payload without verification
    const payload = typeof tokenOrPayload === "string" ? decodeJwt(tokenOrPayload) : tokenOrPayload;

    // Check if the token has an expiration claim
    if (!payload.exp) {
      // If there's no expiration claim, we can't determine if it's expired
      // Some implementations might choose to treat this as expired for security
      return true;
    }

    // Get current time in seconds (JWT exp is in seconds)
    const currentTime = Math.floor(Date.now() / 1000);

    // Compare current time with expiration time
    // If currentTime > exp, the token is expired
    return currentTime > payload.exp;
  } catch (error) {
    // If token can't be decoded, consider it expired/invalid
    console.error("Error decoding JWT token:", error);
    return true;
  }
};


/** Initializes a client in a server function which has admin privileges
 */
export const createAccessToken = async (
  userProfileId: string
): Promise<string> => {

  if (!process.env.SUPABASE_JWT_SECRET) {
    throw new Error("Secret key is not defined");
  }

  const secret = Buffer.from(process.env.SUPABASE_JWT_SECRET!, "utf-8").toString("base64url");

  // Convert your secret key to a format that can be used with jose
  const key = await importJWK({ kty: "oct", k: secret });

  const jwt = await new SignJWT({
    role: "authenticated",
    aal: "aal1"
  })
    .setAudience("authenticated")
    .setSubject(userProfileId)
    .setProtectedHeader({ alg: "HS256" }) // Specify the algorithm
    .setIssuedAt() // Set the issued at time
    .setExpirationTime("2h") // Set an expiration time (e.g., 2 hours from now)
    .sign(key);

  return jwt;
};

export const verifyAccessTokenForUserId = async (
  accessToken: string
): Promise<Result<{ userId?: string }>> => {
  if (!process.env.SUPABASE_JWT_SECRET) {
    return err(new Error("Internal error"));
  }

  let payload: JWTPayload;
  try {
    const secret = Buffer.from(process.env.SUPABASE_JWT_SECRET, "utf-8").toString("base64url");
    // Convert secret to JWK format for verification
    const key = await importJWK({ kty: "oct", k: secret });
    // Verify the JWT
    const { payload: _payload } = await jwtVerify(accessToken, key, {
      audience: "authenticated",
      algorithms: [ "HS256" ]
    });
    payload = _payload;
  } catch (error) {
    return err(error instanceof Error ? error : new Error("Unknown error"));
  }

  // Verify required claims
  if (!payload.sub) {
    return err(new Error("Missing subject (user ID) in token"));
  } else if (payload.role !== "authenticated") {
    return err(new Error("Invalid role claim in token"));
  } else if (payload.aal !== "aal1") {
    return err(new Error("Invalid aal claim in token"));
  }

  return ok({ userId: payload.sub });
};